import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Checkbox,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Divider,
  TextField,
  TablePagination,
  InputAdornment,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { IClient, IClientLog, ClientStatus, CLIENT_STATUS_LABELS, CLIENT_STATUS_COLORS } from '../../types';

const CommercialDashboard: React.FC = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [availableClients, setAvailableClients] = useState<IClient[]>([]);
  const [totalAvailable, setTotalAvailable] = useState(0);
  const [myClients, setMyClients] = useState<IClient[]>([]);
  const [totalMyClients, setTotalMyClients] = useState(0);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<IClient | null>(null);
  const [clientLogs, setClientLogs] = useState<IClientLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // Filtros e paginação para disponíveis
  const [searchAvailable, setSearchAvailable] = useState('');
  const [pageAvailable, setPageAvailable] = useState(0);
  const [rowsPerPageAvailable, setRowsPerPageAvailable] = useState(10);

  // Filtros e paginação para meus clientes
  const [searchMyClients, setSearchMyClients] = useState('');
  // Por padrão, mostra apenas clientes em andamento (pendente, call agendada, no-show)
  const [statusFilter, setStatusFilter] = useState<ClientStatus[]>([
    'pending_contact',
    'call_scheduled',
    'no_show',
  ]);
  const [pageMyClients, setPageMyClients] = useState(0);
  const [rowsPerPageMyClients, setRowsPerPageMyClients] = useState(10);

  const fetchAvailableClients = useCallback(async () => {
    try {
      const params: any = {
        skip: pageAvailable * rowsPerPageAvailable,
        limit: rowsPerPageAvailable,
      };
      if (searchAvailable) params.search = searchAvailable;

      const response = await api.get('/clients/available', { params });
      setAvailableClients(response.data.data.clients);
      setTotalAvailable(response.data.data.total);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar clientes disponíveis');
    }
  }, [pageAvailable, rowsPerPageAvailable, searchAvailable]);

  const fetchMyClients = useCallback(async () => {
    try {
      const params: any = {
        skip: pageMyClients * rowsPerPageMyClients,
        limit: rowsPerPageMyClients,
      };
      if (searchMyClients) params.search = searchMyClients;
      // Envia os status como string separada por vírgula
      if (statusFilter.length > 0) params.status = statusFilter.join(',');

      const response = await api.get('/clients/assigned', { params });
      setMyClients(response.data.data.clients);
      setTotalMyClients(response.data.data.total);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar meus clientes');
    }
  }, [pageMyClients, rowsPerPageMyClients, searchMyClients, statusFilter]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchAvailableClients(), fetchMyClients()]);
      setLoading(false);
    };
    loadData();
  }, [fetchAvailableClients, fetchMyClients]);

  useEffect(() => {
    if (!loading) {
      fetchAvailableClients();
    }
  }, [pageAvailable, rowsPerPageAvailable, searchAvailable, fetchAvailableClients, loading]);

  useEffect(() => {
    if (!loading) {
      fetchMyClients();
    }
  }, [pageMyClients, rowsPerPageMyClients, searchMyClients, statusFilter, fetchMyClients, loading]);

  // Debounce para busca
  useEffect(() => {
    const timer = setTimeout(() => {
      setPageAvailable(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchAvailable]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPageMyClients(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchMyClients]);

  const handleStatusFilterChange = (values: ClientStatus[]) => {
    setStatusFilter(values);
    setPageMyClients(0);
  };

  const handleSelectClient = (clientId: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(clientId)) {
        return prev.filter((id) => id !== clientId);
      }
      if (prev.length >= 20) {
        setError('Máximo de 20 clientes por vez');
        return prev;
      }
      return [...prev, clientId];
    });
  };

  const handleAssignClients = async () => {
    if (selectedIds.length === 0) return;

    try {
      setAssigning(true);
      setError('');
      await api.post('/clients/assign', { client_ids: selectedIds });
      setSuccess(`${selectedIds.length} cliente(s) atribuído(s) com sucesso!`);
      setSelectedIds([]);
      fetchAvailableClients();
      fetchMyClients();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao atribuir clientes');
    } finally {
      setAssigning(false);
    }
  };

  const handleStatusChange = async (clientId: string, newStatus: ClientStatus) => {
    try {
      await api.patch(`/clients/update-status/${clientId}`, { status: newStatus });
      setSuccess('Status atualizado com sucesso!');
      fetchMyClients();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao atualizar status');
    }
  };

  const handleViewDetails = async (client: IClient) => {
    setSelectedClient(client);
    setDetailsOpen(true);
    setLoadingLogs(true);

    try {
      const response = await api.get(`/clients/get/${client.id}`);
      setClientLogs(response.data.data.logs);
    } catch {
      setClientLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  const refreshAll = () => {
    fetchAvailableClients();
    fetchMyClients();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Painel Comercial
        </Typography>
        <IconButton onClick={refreshAll} title="Atualizar">
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 3 }}>
        <Tab label={`Disponíveis (${totalAvailable})`} />
        <Tab label={`Meus Clientes (${totalMyClients})`} />
      </Tabs>

      {tabValue === 0 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Buscar por nome, email ou telefone..."
              value={searchAvailable}
              onChange={(e) => setSearchAvailable(e.target.value)}
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            {selectedIds.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography>
                  {selectedIds.length} cliente(s) selecionado(s) (máx. 20)
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleAssignClients}
                  disabled={assigning}
                >
                  {assigning ? <CircularProgress size={20} /> : 'Atribuir a mim'}
                </Button>
              </Box>
            )}
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Nome</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Telefone</TableCell>
                  <TableCell>Indicado por</TableCell>
                  <TableCell>Data</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {availableClients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        {searchAvailable
                          ? 'Nenhum cliente encontrado com os filtros aplicados'
                          : 'Nenhum cliente disponível no momento'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  availableClients.map((client) => (
                    <TableRow key={client.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.includes(client.id)}
                          onChange={() => handleSelectClient(client.id)}
                        />
                      </TableCell>
                      <TableCell>{client.name}</TableCell>
                      <TableCell>{client.email}</TableCell>
                      <TableCell>{client.phone}</TableCell>
                      <TableCell>{client.affiliate?.name || '-'}</TableCell>
                      <TableCell>
                        {new Date(client.created_at).toLocaleDateString('pt-BR')}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalAvailable}
              page={pageAvailable}
              onPageChange={(_, newPage) => setPageAvailable(newPage)}
              rowsPerPage={rowsPerPageAvailable}
              onRowsPerPageChange={(e) => {
                setRowsPerPageAvailable(parseInt(e.target.value, 10));
                setPageAvailable(0);
              }}
              labelRowsPerPage="Linhas por página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
              }
            />
          </TableContainer>
        </Box>
      )}

      {tabValue === 1 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Buscar por nome, email ou telefone..."
              value={searchMyClients}
              onChange={(e) => setSearchMyClients(e.target.value)}
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Select
              size="small"
              multiple
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value as ClientStatus[])}
              displayEmpty
              sx={{ minWidth: 250 }}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <Typography color="text.secondary">Todos os status</Typography>;
                }
                if (selected.length === Object.keys(CLIENT_STATUS_LABELS).length) {
                  return 'Todos os status';
                }
                return selected.map((s) => CLIENT_STATUS_LABELS[s]).join(', ');
              }}
            >
              {Object.entries(CLIENT_STATUS_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  <Checkbox checked={statusFilter.includes(value as ClientStatus)} />
                  {label}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Telefone</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Indicado por</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {myClients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        {searchMyClients || statusFilter.length > 0
                          ? 'Nenhum cliente encontrado com os filtros aplicados'
                          : 'Você não tem clientes atribuídos'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  myClients.map((client) => (
                    <TableRow key={client.id}>
                      <TableCell>{client.name}</TableCell>
                      <TableCell>{client.email}</TableCell>
                      <TableCell>{client.phone}</TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={client.status}
                          onChange={(e) =>
                            handleStatusChange(client.id, e.target.value as ClientStatus)
                          }
                        >
                          {Object.entries(CLIENT_STATUS_LABELS).map(([value, label]) => (
                            <MenuItem key={value} value={value}>
                              {label}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>{client.affiliate?.name || '-'}</TableCell>
                      <TableCell>
                        <Tooltip title="Ver detalhes e histórico">
                          <IconButton onClick={() => handleViewDetails(client)}>
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalMyClients}
              page={pageMyClients}
              onPageChange={(_, newPage) => setPageMyClients(newPage)}
              rowsPerPage={rowsPerPageMyClients}
              onRowsPerPageChange={(e) => {
                setRowsPerPageMyClients(parseInt(e.target.value, 10));
                setPageMyClients(0);
              }}
              labelRowsPerPage="Linhas por página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
              }
            />
          </TableContainer>
        </Box>
      )}

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon color="primary" />
            Detalhes do Cliente - {selectedClient?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedClient && (
            <Box>
              <Box sx={{ mb: 3, p: 2, bgcolor: theme.palette.background.default, borderRadius: 1 }}>
                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mb: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Email
                    </Typography>
                    <Typography>{selectedClient.email}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Telefone
                    </Typography>
                    <Typography>{selectedClient.phone}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Status Atual
                    </Typography>
                    <Box>
                      <Chip
                        label={CLIENT_STATUS_LABELS[selectedClient.status]}
                        color={CLIENT_STATUS_COLORS[selectedClient.status]}
                        size="small"
                      />
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Indicado por
                    </Typography>
                    <Typography>{selectedClient.affiliate?.name || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Data de Cadastro
                    </Typography>
                    <Typography>
                      {new Date(selectedClient.created_at).toLocaleString('pt-BR')}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon fontSize="small" />
                Histórico de Atividades
              </Typography>

              {loadingLogs ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : clientLogs.length === 0 ? (
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  Nenhum registro de atividade encontrado
                </Typography>
              ) : (
                <List sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                  {clientLogs.map((log, index) => (
                    <React.Fragment key={log.id}>
                      <ListItem alignItems="flex-start">
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography fontWeight="medium">
                                {log.description}
                              </Typography>
                              {log.action === 'STATUS_CHANGED' && (
                                <Chip
                                  label={`${log.previous_value} → ${log.new_value}`}
                                  size="small"
                                  variant="outlined"
                                  color="info"
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">
                                {new Date(log.created_at).toLocaleString('pt-BR')}
                                {log.performed_by && ` • Realizado por: ${log.performed_by.name}`}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < clientLogs.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CommercialDashboard;
